import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Send, ArrowLeft, CheckCircle, Check, MessageSquare } from 'lucide-react';
import { api } from '../services/api';
import type { Contact } from '../types';

type Step = 'search' | 'compose' | 'sent';

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1);

const STEPS = [
  { id: 'search',  label: 'Search' },
  { id: 'compose', label: 'Compose' },
  { id: 'sent',    label: 'Sent' },
];

const QuickSendPage: React.FC = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('search');
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Contact[]>([]);
  const [recentContacts, setRecentContacts] = useState<Contact[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<Contact[]>([]);
  const [toInput, setToInput] = useState('');
  const [recipients, setRecipients] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const searchRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const stepIndex = step === 'search' ? 0 : step === 'compose' ? 1 : 2;

  useEffect(() => {
    const t = setTimeout(() => searchInputRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    api.searchContacts({ limit: 20, active_only: true }).then((res) => {
      setRecentContacts(res.data ?? []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!q.trim()) {
      setResults(recentContacts);
      return;
    }
    if (searchRef.current) clearTimeout(searchRef.current);
    searchRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.searchContacts({ q, limit: 10 });
        setResults(res.data ?? []);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, [q, recentContacts]);

  const toggleContact = (c: Contact) => {
    setSelected((prev) =>
      prev.some((x) => x.id === c.id)
        ? prev.filter((x) => x.id !== c.id)
        : [...prev, c]
    );
  };

  const removeContact = (id: string) => {
    setSelected((prev) => prev.filter((x) => x.id !== id));
  };

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const addRecipient = (email: string) => {
    const e = email.trim().toLowerCase();
    if (EMAIL_RE.test(e) && !recipients.includes(e)) {
      setRecipients((prev) => [...prev, e]);
      setToInput('');
    }
  };

  const handleToKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'Enter' || ev.key === ',' || ev.key === 'Tab') {
      ev.preventDefault();
      addRecipient(toInput);
    }
    if (ev.key === 'Backspace' && toInput === '' && recipients.length > 0) {
      setRecipients((prev) => prev.slice(0, -1));
    }
  };

  const handleToBlur = () => {
    if (toInput.trim()) addRecipient(toInput);
  };

  const displayName = (c: Contact) =>
    c.full_name || `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim();

  const handleSend = async () => {
    if (!selected.length || !recipients.length) return;
    setSending(true);
    setError('');
    try {
      await api.sendContacts({
        contact_ids: selected.map((c) => c.id),
        recipients,
        message:     message || null,
      });
      setStep('sent');
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to send.');
    } finally {
      setSending(false);
    }
  };

  const handleSendSMS = () => {
    const blocks = selected.map((c) => {
      const phone = c.phones?.find((p) => p.is_preferred)?.phone_number ?? c.preferred_phones?.[0];
      return [
        displayName(c),
        c.title ?? null,
        c.agency_acronym ?? c.agency_name ?? null,
        phone ? `📞 ${phone}` : null,
        (c as any).email_primary ? `✉ ${(c as any).email_primary}` : null,
      ].filter(Boolean).join('\n');
    });
    window.location.href = `sms:?body=${encodeURIComponent(blocks.join('\n\n'))}`;
  };

  const reset = () => {
    setStep('search');
    setQ('');
    setResults(recentContacts);
    setSelected([]);
    setToInput('');
    setRecipients([]);
    setMessage('');
    setError('');
  };

  return (
    // IMPORTANT: Must be the dark sidebar colour (--nycem-blue), NOT var(--navy),
    // which is the brighter link/button blue. The white text below assumes dark.
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--nycem-blue)',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 36,
      }}
    >
      {/* Top bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          height: 56,
          gap: 12,
          padding: '0 16px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <button
          onClick={() => step === 'compose' ? setStep('search') : navigate(-1)}
          style={{
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.7)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 14,
          }}
        >
          <ArrowLeft size={16} />
          {step === 'compose' ? 'Back to Search' : 'Back'}
        </button>
        <span style={{ color: 'white', fontWeight: 700, fontSize: 16, marginLeft: 4 }}>
          Quick Send
        </span>
      </div>

      {/* IMPORTANT: Step bar must be its own div BELOW the topbar with
          justifyContent: center. Do not move inside topbar row or use
          marginLeft: auto — that pushes it to the right edge. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '14px 24px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          flexShrink: 0,
        }}
      >
        {STEPS.map((s, i) => {
          const isActive = i === stepIndex;
          const isCompleted = i < stepIndex;
          return (
            <React.Fragment key={s.id}>
              {i > 0 && (
                <div style={{ width: 48, height: 2, background: 'rgba(255,255,255,0.15)', margin: '0 4px' }} />
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: isCompleted ? 'var(--green)' : isActive ? 'var(--hazard-deep)' : 'rgba(255,255,255,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    fontWeight: 700,
                    color: 'white',
                  }}
                >
                  {i + 1}
                </div>
                <span
                  style={{
                    fontSize: 14,
                    color: isActive ? 'white' : 'rgba(255,255,255,0.72)',
                    fontWeight: isActive ? 600 : 400,
                  }}
                >
                  {s.label}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Main content */}
      <div style={{ padding: '0 16px 24px', flex: 1 }}>
        <div style={{ maxWidth: 480, width: '100%', margin: '0 auto', paddingTop: 16, paddingBottom: selected.length > 0 && step === 'search' ? 80 : 0 }}>
          {step === 'sent' ? (
            <div
              style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: 12,
                padding: 32,
                textAlign: 'center',
                color: 'white',
                marginTop: 24,
              }}
            >
              <CheckCircle size={48} color="#4ade80" style={{ marginBottom: 16 }} />
              <h2 style={{ margin: '0 0 8px', color: 'var(--white)' }}>Sent!</h2>
              <p style={{ margin: '0 0 24px', color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>
                {selected.length === 1
                  ? `Contact info for ${displayName(selected[0])}`
                  : `Contact info for ${selected.length} contacts`}
                {' '}was sent to {recipients.length === 1 ? recipients[0] : `${recipients.length} recipients`}.
              </p>
              <button className="btn btn-primary" onClick={reset} style={{ width: '100%' }}>
                Send Another
              </button>
            </div>
          ) : step === 'compose' ? (
            <>
              {/* Selected contacts list */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ color: 'rgba(255,255,255,0.72)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                  Sending {selected.length} contact{selected.length !== 1 ? 's' : ''}
                </div>
                {selected.map((c) => (
                  <div
                    key={c.id}
                    style={{
                      background: 'rgba(255,255,255,0.06)',
                      borderRadius: 8,
                      padding: '10px 14px',
                      marginBottom: 6,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div>
                      <div style={{ color: 'white', fontWeight: 500, fontSize: 14 }}>{displayName(c)}</div>
                      <div style={{ color: 'rgba(255,255,255,0.72)', fontSize: 12 }}>
                        {c.title ? `${c.title} · ` : ''}{c.agency_acronym ?? c.agency_name}
                      </div>
                    </div>
                    <button
                      onClick={() => { removeContact(c.id); if (selected.length === 1) setStep('search'); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)' }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>

              <label style={{ display: 'block', color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                To
              </label>
              {/* Tag-style multi-email input */}
              <div
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 6,
                  padding: '6px 8px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 4,
                  alignItems: 'center',
                  minHeight: 38,
                  cursor: 'text',
                  marginBottom: 4,
                }}
                onClick={(e) => (e.currentTarget.querySelector('input') as HTMLInputElement)?.focus()}
              >
                {recipients.map((email) => (
                  <div key={email} style={{
                    background: 'rgba(255,255,255,0.12)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 4,
                    padding: '2px 6px',
                    fontSize: 12,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}>
                    {email}
                    <span
                      onClick={() => setRecipients((prev) => prev.filter((r) => r !== email))}
                      style={{ cursor: 'pointer', opacity: 0.6, fontSize: 14, lineHeight: 1 }}
                    >×</span>
                  </div>
                ))}
                <input
                  autoFocus
                  value={toInput}
                  onChange={(e) => setToInput(e.target.value)}
                  onKeyDown={handleToKeyDown}
                  onBlur={handleToBlur}
                  placeholder={recipients.length === 0 ? 'Add recipient email…' : ''}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    color: 'white',
                    fontSize: 14,
                    flex: 1,
                    minWidth: 140,
                  }}
                />
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.72)', marginBottom: 12 }}>
                Press Enter or comma to add. Multiple recipients supported.
              </div>

              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.72)', margin: '0 0 12px', lineHeight: 1.5 }}>
                {selected.length === 1 ? '1 contact' : `${selected.length} contacts`} will be attached as vCard (.vcf) files.
              </p>

              <label style={{ display: 'block', color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                Message (optional)
              </label>
              <textarea
                placeholder="Add a note…"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 8,
                  color: 'white',
                  fontSize: 14,
                  outline: 'none',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  marginBottom: 16,
                }}
              />

              {error && (
                <p style={{ color: '#f87171', fontSize: 14, marginBottom: 12 }}>{error}</p>
              )}

              <button
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center', padding: '12px', fontSize: 15 }}
                disabled={recipients.length === 0 || sending}
                onClick={handleSend}
              >
                <Send size={16} />
                {sending ? 'Sending…' : `Send ${selected.length} Contact${selected.length !== 1 ? 's' : ''}`}
              </button>

              {isMobile && selected.length >= 1 && (
                <button
                  onClick={handleSendSMS}
                  style={{
                    width: '100%',
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 6,
                    padding: '10px',
                    color: 'rgba(255,255,255,0.75)',
                    fontSize: 14,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    marginTop: 8,
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                >
                  <MessageSquare size={14} />
                  Send via SMS
                </button>
              )}
            </>
          ) : (
            <>
              {/* Search step */}
              <div
                style={{
                  background: 'rgba(255,255,255,0.08)',
                  borderRadius: 10,
                  padding: '10px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <Search size={16} color="rgba(255,255,255,0.5)" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search by name, title, or agency acronym (e.g. NYPD, FDNY)…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  style={{
                    background: 'none',
                    border: 'none',
                    outline: 'none',
                    color: 'white',
                    fontSize: 15,
                    flex: 1,
                  }}
                />
                {q && (
                  <button
                    onClick={() => { setQ(''); setResults(recentContacts); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.5)' }}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              {searching && (
                <p style={{ color: 'rgba(255,255,255,0.72)', fontSize: 14, textAlign: 'center', margin: '16px 0' }}>
                  Searching…
                </p>
              )}

              {!q && results.length > 0 && (
                <p style={{ color: 'rgba(255,255,255,0.72)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px' }}>
                  Recent
                </p>
              )}

              {results.map((c) => {
                const isChecked = selected.some((x) => x.id === c.id);
                const preferredPhone = c.phones?.find((p) => p.is_preferred)?.phone_number
                  ?? c.preferred_phones?.[0]
                  ?? null;
                return (
                  <div
                    key={c.id}
                    onClick={() => toggleContact(c)}
                    style={{
                      background: isChecked ? 'rgba(30,58,138,0.6)' : 'rgba(255,255,255,0.05)',
                      borderRadius: 8,
                      padding: '12px 14px',
                      marginBottom: 8,
                      cursor: 'pointer',
                      border: isChecked ? '1px solid rgba(99,102,241,0.4)' : '1px solid rgba(255,255,255,0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                    }}
                  >
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: 4,
                        border: '2px solid',
                        borderColor: isChecked ? '#4ade80' : 'rgba(255,255,255,0.3)',
                        background: isChecked ? '#4ade80' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {isChecked && <Check size={12} color="#1a2940" strokeWidth={3} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: 'white', fontWeight: 600, marginBottom: 2 }}>
                        {displayName(c)}
                      </div>
                      <div style={{ color: 'rgba(255,255,255,0.72)', fontSize: 14 }}>
                        {c.title ? `${c.title} · ` : ''}{c.agency_acronym ?? c.agency_name}
                      </div>
                      {preferredPhone && (
                        <div style={{ color: 'rgba(255,255,255,0.72)', fontSize: 12, marginTop: 2 }}>
                          {preferredPhone}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {!q && results.length === 0 && (
                <p style={{ color: 'rgba(255,255,255,0.72)', fontSize: 14, textAlign: 'center', margin: '16px 0' }}>
                  Loading recent contacts…
                </p>
              )}
              {q && !searching && results.length === 0 && (
                <p style={{ color: 'rgba(255,255,255,0.72)', fontSize: 14, textAlign: 'center', margin: '16px 0' }}>
                  No contacts found
                </p>
              )}

              {selected.length > 0 && (
                <div
                  style={{
                    marginTop: 12,
                    background: 'var(--electric)',
                    borderRadius: 8,
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, flex: 1 }}>
                    {selected.length} contact{selected.length !== 1 ? 's' : ''} selected
                  </span>
                  <button
                    className="btn btn-ghost"
                    style={{ color: 'rgba(255,255,255,0.72)', fontSize: 14 }}
                    onClick={() => setSelected([])}
                  >
                    Clear
                  </button>
                  <button
                    className="btn btn-primary"
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                    onClick={() => setStep('compose')}
                  >
                    <Send size={14} />
                    Send {selected.length}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickSendPage;
